import { drawLampMote, LAMP_MOTE, type LampRgb } from './lamp_mote'

// 常夜燈の明かりの比較版 v1 (2026-11-03 作者の答え・答えるページ S7dPfoZEANZZxP6ZeMgiT4 の lamp)。
//   作者 (11-03 通し)「HTMLで書いたエフェクト感があるんで、本当にライトが、ジョイア灯がちゃんと光っているような感じにしてほしい」
//   何が＝にじみ・灯りから立つ粒／どう＝平たい丸を置いただけ・明るさが揺れない・まわりが照らされない。
//   直す所 3 つ：
//     ① にじみ＝芯から外へ距離の2乗で減る形 (1/(1+(r/r0)^2))。半径を 3 段 (芯の白・ガラスの青・部屋へ漏れる薄い青) に重ねる。
//     ② 明るさ＝ゆっくり不規則に揺らす (炎の揺らぎ)。周期の合わない3つの滑らかな雑音の和。たまに小さく沈む。
//     ③ 照らす＝灯りの後ろの壁に縦長の淡い照り返し。揺らぎと同じ値で明るさが動く。
//        床の光だまりは floorY を渡した時だけ (今の呼び出しは渡さない＝灯りは奥の壁や柱の上にあり、手前の床に置くと遠近がずれる)。
//   粒＝常夜燈の1粒の正本 (src/lamp_mote.ts・色と大きさは変えない)。灯りから少しずつ立ちのぼり、揺らぎに合わせて瞬く。
//   色＝常夜燈の3色 (LAMP_MOTE)。配布版はこの v1 (11-02 作者の答え「配布版にする」)。DEV ?lampLight=old で前の版 (drawGhostLight の丸1つ)。
let v1 = true
export function setLampLightLook(v: string | null): void { v1 = v !== 'old' }
/** 常夜燈を v1 で描くか (配布版。DEV ?lampLight=old で前の版)。 */
export function lampLightV1(): boolean { return v1 }

const rgba = (rgb: LampRgb, a: number): string => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${Math.max(0, Math.min(1, a)).toFixed(3)})`
const hash = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
// 1次元の滑らかな雑音 0..1 (整数の格子に乱数を置き、 smoothstep でつなぐ)。
function noise1(x: number, seed: number): number {
  const i = Math.floor(x), f = x - i, s = f * f * (3 - 2 * f)
  const a = hash(i + seed * 57.3), b = hash(i + 1 + seed * 57.3)
  return a + (b - a) * s
}

/** 炎の揺らぎ 約 0.78..1.06。 seed で灯りごとにずらす。 */
export function lampFlicker(tSec: number, seed = 0): number {
  const slow = noise1(tSec * 0.55, seed + 1)       // 息をするような遅い揺れ (約2秒)
  const mid = noise1(tSec * 2.1, seed + 2)         // 炎が立ち直る揺れ
  const fast = noise1(tSec * 7.5, seed + 3)        // 細かいちらつき (小さく)
  // たまに沈む: 遅い雑音が低い所だけ、もう一段暗くする (一定の点滅にしない)。
  const dip = Math.max(0, 0.28 - noise1(tSec * 0.33, seed + 4)) * 0.45
  return 0.8 + slow * 0.14 + mid * 0.08 + fast * 0.04 - dip
}

// 距離の2乗で減るにじみを、 色の段で近似する (r0 = 半分の明るさになる半径の目安)。
export function falloffGlow(
  ctx: CanvasRenderingContext2D, x: number, y: number, R: number, r0: number,
  rgb: LampRgb, a: number, sx = 1, sy = 1,
): void {
  if (a <= 0.004 || R <= 0) return
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(sx, sy)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, R)
  const end = 1 / (1 + (R / r0) ** 2)
  for (const t of [0, 0.06, 0.14, 0.25, 0.4, 0.6, 0.8, 1]) {
    // 端で 0 へ落ちるよう、 端の値を引いて伸ばす (円の縁が見えないように)。
    const v = (1 / (1 + ((t * R) / r0) ** 2) - end) / (1 - end)
    g.addColorStop(t, rgba(rgb, a * v))
  }
  ctx.fillStyle = g
  ctx.fillRect(-R, -R, R * 2, R * 2)
  ctx.restore()
}

export type LampLightOpts = {
  /** 光だまりを落とす床の y (無ければ床を照らさない = 床の無い画面)。 */
  floorY?: number
  /** 灯りごとに揺らぎをずらす番号。 */
  seed?: number
  /** 後ろの壁を照らすか (既定 true)。 壁の無い暗い画面 (終幕の2択など) では切る = 縦の光の柱に見えるため (11-03 撮影)。 */
  wall?: boolean
  /** 立ちのぼる粒を出すか (既定 true)。 */
  motes?: boolean
}

/**
 * 常夜燈の明かり v1。 (x, y) = 灯芯。 scale = 部屋の倍率 (画面 960x540 上の大きさ × scale)。 intensity = 濃さ。
 * 加算で描く (呼ぶ側の合成はこの中で保存・復元する)。
 */
export function drawLampLightV1(
  ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, intensity: number,
  opts: LampLightOpts = {},
): void {
  const tm = performance.now() / 1000
  const seed = opts.seed ?? 0
  const f = lampFlicker(tm, seed)
  // にじみと照り返しの明るさ (11-03 作者「ちょっと明るすぎるな。」で 1 → 0.65)。 芯の点と粒は下げない (光の出どころは残す)。
  const GAIN = 0.65
  const I = intensity * f * GAIN
  const S = scale
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // ③ 壁の照り返し: 灯りの後ろの石が縦長に淡く明るむ (灯りより少し下へ寄せる = 光は下へ広がって見える)。
  if (opts.wall ?? true) falloffGlow(ctx, x, y + 26 * S, 150 * S, 40 * S, LAMP_MOTE.deep, 0.2 * I, 0.8, 1.5)
  // ③ 床の光だまり: 灯りの真下の床に平たい楕円。 灯りが高いほど広く淡い。
  if (opts.floorY !== undefined && opts.floorY > y) {
    const h = opts.floorY - y
    const spread = Math.min(220, 70 + h * 0.75) * (h > 0 ? 1 : 0)
    const a = 0.2 * I * Math.max(0.35, Math.min(1, 160 / Math.max(1, h)))
    falloffGlow(ctx, x, opts.floorY + 2 * S, spread, spread * 0.3, LAMP_MOTE.deep, a, 1, 0.13)
    falloffGlow(ctx, x, opts.floorY + 1 * S, spread * 0.45, spread * 0.14, LAMP_MOTE.mid, a * 0.55, 1, 0.12)
  }
  // ① にじみ 3 段: 部屋へ漏れる薄い青 → ガラスの青 → 芯のまわりの白。
  //   芯に近い段ほど揺らぎが強く効く (炎そのものが揺れ、 遠くの照り返しは鈍く揺れる)。
  const near = intensity * (0.6 + (f - 0.6) * 1.6)
  falloffGlow(ctx, x, y, 64 * S, 12 * S, LAMP_MOTE.deep, 0.42 * I)
  falloffGlow(ctx, x, y, 26 * S, 5 * S, LAMP_MOTE.mid, 0.75 * near * GAIN)
  falloffGlow(ctx, x, y, 9 * S, 2.2 * S, LAMP_MOTE.core, 0.95 * near * GAIN)
  // 芯: ほぼ白の小さな点 (灯芯の座標そのもの)。
  ctx.fillStyle = rgba([236, 244, 255], 0.9 * Math.min(1, near))
  ctx.beginPath(); ctx.arc(x, y, 1.9 * S, 0, Math.PI * 2); ctx.fill()
  // ② 立ちのぼる粒: 灯りの上から少しずつ生まれ、 熱でゆらぎながら昇って消える。 同時に 2〜3 粒。
  //   生まれる位置を横へ散らす (11-03 撮影: 横を揃えると縦一列の点＝噴水に見えた)。
  if (opts.motes ?? true) {
    const N = 5
    for (let k = 0; k < N; k += 1) {
      const period = 3.6 + hash(k * 3.1 + seed) * 2.8
      const raw = tm / period + hash(k * 7.7 + seed)
      const ph = raw - Math.floor(raw)
      const loop = Math.floor(raw)
      // 半分ほどの周期は休ませる (粒が出続けない = 噴水に見せない)。
      if (hash(loop * 13.1 + k + seed) < 0.5) continue
      const ox = (hash(loop * 5.3 + k) - 0.5) * 24 * S
      const rise = ph * (44 + hash(k + seed * 2) * 30) * S
      const sway = Math.sin(tm * (1.3 + hash(k) * 0.9) + k * 2.1) * (3 + ph * 11) * S
      const px = x + ox + sway
      const py = y - 6 * S - rise
      // 生まれてすぐ明るく、 昇るほど薄れる。 炎の揺らぎに合わせて瞬く。
      const life = Math.min(1, ph / 0.12) * (1 - ph) ** 1.4
      drawLampMote(ctx, px, py, 0.5 * intensity * life * (0.7 + 0.3 * f))
    }
  }
  ctx.restore()
}
